import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';

const AuthContext = createContext(null);

const IS_CLOUD = process.env.REACT_APP_DEPLOYMENT_MODE === 'cloud';

let _supabase = null;
function getSupabase() {
  if (!_supabase && IS_CLOUD) {
    _supabase = createClient(
      process.env.REACT_APP_SUPABASE_URL,
      process.env.REACT_APP_SUPABASE_ANON_KEY,
    );
  }
  return _supabase;
}

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userEmail, setUserEmail] = useState(null);

  // ── Cloud mode helpers ────────────────────────────────────────────────────

  const _checkCloudAuth = useCallback(async () => {
    const supabase = getSupabase();
    if (!supabase) return false;
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setUserEmail(session.user.email);
      return true;
    }
    return false;
  }, []);

  // ── Local mode helpers ────────────────────────────────────────────────────

  const _checkLocalAuth = useCallback(async () => {
    const baseUrl = IS_CLOUD
      ? (process.env.REACT_APP_API_URL || '').replace(/\/$/, '')
      : '';
    const response = await fetch(`${baseUrl}/api/auth/check`, {
      credentials: IS_CLOUD ? 'omit' : 'include',
    });
    if (response.ok) {
      const data = await response.json();
      return data.authenticated === true;
    }
    return false;
  }, []);

  // ── Startup auth check ────────────────────────────────────────────────────

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const authed = IS_CLOUD ? await _checkCloudAuth() : await _checkLocalAuth();
        setIsAuthenticated(authed);
      } catch {
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuthStatus();

    // Cloud: subscribe to Supabase auth state changes (token refresh, sign-out)
    if (IS_CLOUD) {
      const supabase = getSupabase();
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (_event, session) => {
          setIsAuthenticated(!!session);
          setUserEmail(session?.user?.email || null);
        }
      );
      return () => subscription.unsubscribe();
    }
  }, [_checkCloudAuth, _checkLocalAuth]);

  // ── Login ─────────────────────────────────────────────────────────────────

  const loginWithPassword = async (password) => {
    const baseUrl = IS_CLOUD
      ? (process.env.REACT_APP_API_URL || '').replace(/\/$/, '')
      : '';
    const response = await fetch(`${baseUrl}/api/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({ password }),
      credentials: IS_CLOUD ? 'omit' : 'include',
    });
    if (response.ok) setIsAuthenticated(true);
    return response;
  };

  const loginWithGoogle = async () => {
    const supabase = getSupabase();
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
    if (error) throw error;
    // Redirect happens automatically — no return value needed
  };

  const login = IS_CLOUD ? loginWithGoogle : loginWithPassword;

  // ── Logout ────────────────────────────────────────────────────────────────

  const logout = async () => {
    if (IS_CLOUD) {
      const supabase = getSupabase();
      await supabase?.auth.signOut();
    } else {
      const baseUrl = IS_CLOUD
        ? (process.env.REACT_APP_API_URL || '').replace(/\/$/, '')
        : '';
      await fetch(`${baseUrl}/api/logout`, { method: 'POST', credentials: IS_CLOUD ? 'omit' : 'include' });
    }
    setIsAuthenticated(false);
    setUserEmail(null);
  };

  // ── Token accessor (used by api.js interceptor in cloud mode) ─────────────

  const getToken = async () => {
    if (!IS_CLOUD) return null;
    const supabase = getSupabase();
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  };

  return (
    <AuthContext.Provider value={{
      isAuthenticated, isLoading, userEmail,
      login, logout, getToken, isCloud: IS_CLOUD,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
